import React, { useState, useEffect } from 'react';

/**
 * BulkOperationsPanel - Enable staff to perform actions on multiple requests
 *
 * Operations supported:
 * - Bulk Assign: Assign multiple unassigned requests to a staff member
 * - Bulk Transition: Move multiple requests to next status
 * - Bulk Decline: Decline multiple requests with same reason
 * - Bulk Close: Archive multiple completed requests
 */

const BulkOperationsPanel = ({
  selectedRequests = [],
  onClose,
  onOperationComplete,
  staffMembers = [],
  darkMode = false
}) => {
  const [operation, setOperation] = useState('assign');
  const [params, setParams] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Operation configurations
  const operations = {
    assign: {
      label: 'Bulk Assign',
      description: 'Assign selected requests to a staff member',
      icon: '👤',
      applicableStatuses: ['SUBMITTED', 'TRIAGE'],
      params: ['staff_id', 'notes']
    },
    transition: {
      label: 'Bulk Transition',
      description: 'Move requests to the next status',
      icon: '➡️',
      applicableStatuses: ['SUBMITTED', 'TRIAGE', 'IN_REVIEW', 'APPROVED', 'STAGED'],
      params: ['target_status', 'notes']
    },
    decline: {
      label: 'Bulk Decline',
      description: 'Decline multiple requests with the same reason',
      icon: '❌',
      applicableStatuses: ['SUBMITTED', 'TRIAGE', 'IN_REVIEW'],
      params: ['reason_code', 'notes']
    },
    close: {
      label: 'Bulk Close',
      description: 'Archive completed or expired requests',
      icon: '📁',
      applicableStatuses: ['EXPIRED', 'WITHDRAWN', 'DECLINED', 'CONNECTED'],
      params: ['outcome', 'notes']
    }
  };

  // Status transition options
  const statusTransitions = {
    'SUBMITTED': ['TRIAGE', 'DECLINED'],
    'TRIAGE': ['IN_REVIEW', 'DECLINED'],
    'IN_REVIEW': ['APPROVED', 'DECLINED', 'TRIAGE'],
    'APPROVED': ['STAGED', 'DECLINED', 'IN_REVIEW'],
    'STAGED': ['SENT', 'IN_REVIEW']
  };

  // Decline reason codes
  const declineReasons = [
    { code: 'no_match', label: 'No suitable match available' },
    { code: 'duplicate', label: 'Duplicate request' },
    { code: 'ineligible', label: 'Requester not eligible' },
    { code: 'capacity', label: 'Capacity constraints' },
    { code: 'other', label: 'Other reason' }
  ];

  // Close outcomes
  const closeOutcomes = [
    { code: 'successful', label: 'Successful connection' },
    { code: 'unsuccessful', label: 'Unsuccessful - no connection made' },
    { code: 'expired', label: 'Request expired' },
    { code: 'withdrawn', label: 'Withdrawn by requester' }
  ];

  const getToken = () => localStorage.getItem('token');

  // Fetch preview when operation or params change
  const fetchPreview = async () => {
    if (selectedRequests.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/staff/brokerage/bulk-operation/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          request_ids: selectedRequests.map(r => r.id),
          operation,
          params
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch preview');
      }

      const data = await response.json();
      setPreview(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset params when operation changes
    setParams({});
    setPreview(null);
    setResult(null);
  }, [operation]);

  // Execute bulk operation
  const executeOperation = async () => {
    setExecuting(true);
    setError(null);

    try {
      const response = await fetch('/api/staff/brokerage/bulk-operation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          request_ids: selectedRequests.map(r => r.id),
          operation,
          params
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Bulk operation failed');
      }

      const data = await response.json();
      setResult(data);

      if (onOperationComplete) {
        onOperationComplete(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  };

  // Check if operation can be applied to selected requests
  const getApplicableRequests = () => {
    const applicableStatuses = operations[operation].applicableStatuses;
    return selectedRequests.filter(r => applicableStatuses.includes(r.status));
  };

  const applicableCount = getApplicableRequests().length;
  const inapplicableCount = selectedRequests.length - applicableCount;

  // Validate params for current operation
  const isParamsValid = () => {
    switch (operation) {
      case 'assign':
        return !!params.staff_id;
      case 'transition':
        return !!params.target_status;
      case 'decline':
        return !!params.reason_code;
      case 'close':
        return !!params.outcome;
      default:
        return false;
    }
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${darkMode ? 'bg-black/70' : 'bg-black/50'}`}>
      <div className={`w-full max-w-2xl rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <span className="text-2xl">⚡</span>
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Bulk Operations
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Selection Summary */}
          <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-blue-800'}`}>
              <strong>{selectedRequests.length}</strong> requests selected
              {inapplicableCount > 0 && (
                <span className="text-amber-500 ml-2">
                  ({inapplicableCount} not applicable for this operation)
                </span>
              )}
            </p>
          </div>

          {/* Operation Selection */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Operation
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(operations).map(([key, op]) => (
                <button
                  key={key}
                  onClick={() => setOperation(key)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    operation === key
                      ? darkMode
                        ? 'border-indigo-500 bg-indigo-900/30 text-indigo-300'
                        : 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : darkMode
                        ? 'border-gray-600 hover:border-gray-500 text-gray-300'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>{op.icon}</span>
                    <span className="font-medium">{op.label}</span>
                  </div>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {op.description}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Operation-specific Parameters */}
          <div className={`p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}>
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {operations[operation].label} Parameters
            </h3>

            {/* Assign Parameters */}
            {operation === 'assign' && (
              <div className="space-y-3">
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Assign To *
                  </label>
                  <select
                    value={params.staff_id || ''}
                    onChange={(e) => setParams({ ...params, staff_id: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-gray-600 border-gray-500 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select staff member...</option>
                    {staffMembers.map(staff => (
                      <option key={staff.id} value={staff.id}>
                        {staff.full_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Transition Parameters */}
            {operation === 'transition' && (
              <div className="space-y-3">
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Target Status *
                  </label>
                  <select
                    value={params.target_status || ''}
                    onChange={(e) => setParams({ ...params, target_status: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-gray-600 border-gray-500 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select target status...</option>
                    <option value="TRIAGE">Triage</option>
                    <option value="IN_REVIEW">In Review</option>
                    <option value="APPROVED">Approved</option>
                    <option value="STAGED">Staged</option>
                    <option value="SENT">Sent</option>
                  </select>
                </div>
              </div>
            )}

            {/* Decline Parameters */}
            {operation === 'decline' && (
              <div className="space-y-3">
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Decline Reason *
                  </label>
                  <select
                    value={params.reason_code || ''}
                    onChange={(e) => setParams({ ...params, reason_code: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-gray-600 border-gray-500 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select reason...</option>
                    {declineReasons.map(reason => (
                      <option key={reason.code} value={reason.code}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Close Parameters */}
            {operation === 'close' && (
              <div className="space-y-3">
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Outcome *
                  </label>
                  <select
                    value={params.outcome || ''}
                    onChange={(e) => setParams({ ...params, outcome: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-gray-600 border-gray-500 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Select outcome...</option>
                    {closeOutcomes.map(outcome => (
                      <option key={outcome.code} value={outcome.code}>
                        {outcome.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Notes (common to all operations) */}
            <div className="mt-3">
              <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Notes (optional)
              </label>
              <textarea
                value={params.notes || ''}
                onChange={(e) => setParams({ ...params, notes: e.target.value })}
                rows={2}
                placeholder="Add notes for this batch operation..."
                className={`w-full px-3 py-2 rounded-lg border ${
                  darkMode
                    ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>
          </div>

          {/* Preview Button */}
          {!preview && !result && (
            <button
              onClick={fetchPreview}
              disabled={!isParamsValid() || loading || applicableCount === 0}
              className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                isParamsValid() && applicableCount > 0
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : darkMode
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {loading ? 'Loading Preview...' : 'Preview Changes'}
            </button>
          )}

          {/* Preview Results */}
          {preview && !result && (
            <div className={`p-4 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}>
              <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Preview
              </h3>

              {preview.will_succeed?.length > 0 && (
                <div className="mb-3">
                  <p className={`text-sm font-medium text-green-500 mb-1`}>
                    ✓ Will succeed ({preview.will_succeed.length})
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {preview.will_succeed.slice(0, 5).map(item => (
                      <p key={item.request_id} className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Request #{item.request_id.slice(0, 8)}... - {item.current_status} → {params.target_status || operations[operation].label}
                      </p>
                    ))}
                    {preview.will_succeed.length > 5 && (
                      <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        ... and {preview.will_succeed.length - 5} more
                      </p>
                    )}
                  </div>
                </div>
              )}

              {preview.will_fail?.length > 0 && (
                <div className="mb-3">
                  <p className={`text-sm font-medium text-red-500 mb-1`}>
                    ✗ Will fail ({preview.will_fail.length})
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {preview.will_fail.slice(0, 5).map(item => (
                      <p key={item.request_id} className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Request #{item.request_id.slice(0, 8)}... - {item.reason}
                      </p>
                    ))}
                    {preview.will_fail.length > 5 && (
                      <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        ... and {preview.will_fail.length - 5} more
                      </p>
                    )}
                  </div>
                </div>
              )}

              {preview.warnings?.length > 0 && (
                <div className={`p-2 rounded ${darkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                  <p className="text-sm text-amber-500">
                    ⚠️ {preview.warnings.join(', ')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Execution Result */}
          {result && (
            <div className={`p-4 rounded-lg ${
              result.failure_count === 0
                ? darkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200'
                : darkMode ? 'bg-amber-900/30 border border-amber-700' : 'bg-amber-50 border border-amber-200'
            }`}>
              <h3 className={`text-sm font-medium mb-2 ${
                result.failure_count === 0 ? 'text-green-500' : 'text-amber-500'
              }`}>
                Operation Complete
              </h3>
              <div className="space-y-1">
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  ✓ <strong>{result.success_count}</strong> requests processed successfully
                </p>
                {result.failure_count > 0 && (
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    ✗ <strong>{result.failure_count}</strong> requests failed
                  </p>
                )}
              </div>

              {result.failures?.length > 0 && (
                <div className="mt-3 max-h-24 overflow-y-auto">
                  {result.failures.map((failure, idx) => (
                    <p key={idx} className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                      {failure.request_id.slice(0, 8)}...: {failure.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className={`p-3 rounded-lg ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex justify-end gap-3 p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg ${
              darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            {result ? 'Close' : 'Cancel'}
          </button>

          {preview && !result && (
            <>
              <button
                onClick={() => {
                  setPreview(null);
                }}
                className={`px-4 py-2 rounded-lg ${
                  darkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Back
              </button>
              <button
                onClick={executeOperation}
                disabled={executing || (preview.will_succeed?.length || 0) === 0}
                className={`px-4 py-2 rounded-lg font-medium ${
                  (preview.will_succeed?.length || 0) > 0
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    : darkMode
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {executing ? 'Executing...' : `Apply to ${preview.will_succeed?.length || 0} Requests`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkOperationsPanel;
